import React from "react";
import { Check, X, Edit2, Trash2, History, Package } from "lucide-react";

const CATEGORY_COLORS = {
  games:       "bg-purple-100 text-purple-700",
  electronics: "bg-blue-100 text-blue-700",
  food:        "bg-amber-100 text-amber-700",
  clothing:    "bg-pink-100 text-pink-700",
  tools:       "bg-slate-100 text-slate-700",
};

function StatusBadge({ value }) {
  const v = (value || "").toLowerCase().replace(/ed$/, "e");
  if (v.includes("activ") || v === "active")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />Active</span>;
  if (v.includes("block"))
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide"><span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />Blocked</span>;
  if (v.includes("reserv"))
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />Reserved</span>;
  return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase">{value || "—"}</span>;
}

function CategoryBadge({ value }) {
  const key = (value || "").toLowerCase();
  const cls = CATEGORY_COLORS[key] || "bg-slate-100 text-slate-600";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${cls}`}>{value || "—"}</span>;
}

const SPECIAL_COLS = ["status", "category"];

function CellValue({ col, value }) {
  const colLower = col.toLowerCase();
  if (colLower === "status") return <StatusBadge value={value} />;
  if (colLower === "category") return <CategoryBadge value={value} />;
  if (colLower === "material no" || colLower === "materialid" || colLower === "material_no")
    return <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded-md text-slate-700 font-bold">{value || "—"}</span>;
  if (colLower === "price" || colLower === "unit price")
    return <span className="font-bold text-slate-700">{value ? `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</span>;
  if (colLower === "qty" || colLower === "quantity")
    return <span className="font-bold text-navy-900">{value ? parseFloat(value).toLocaleString() : "0"}</span>;
  return <span className="text-slate-600">{value || "—"}</span>;
}

export default function InventoryTable({
  materials,
  columns,
  editingId,
  editedRow,
  normalizeKey,
  onEdit,
  onDelete,
  onChange,
  onSave,
  onCancel,
  onStartEdit,
  onViewLogs,
}) {
  if (materials.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Package size={28} className="text-slate-400" />
        </div>
        <p className="font-bold text-slate-600 mb-1">No materials in inventory</p>
        <p className="text-sm text-slate-400">Click "Add Material" to get started.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Desktop table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100">
              {columns.map((col) => (
                <th key={col} className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                  {col}
                </th>
              ))}
              <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {materials.map((mat) => (
              <tr key={mat.id} className="hover:bg-slate-50/60 transition-colors group">
                {columns.map((col) => {
                  const key = normalizeKey(col);
                  return (
                    <td key={col} className="px-5 py-3.5 whitespace-nowrap">
                      {editingId === mat.id ? (
                        <input
                          name={key}
                          value={editedRow[key] || ""}
                          onChange={(e) => onChange(e, onEdit)}
                          className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-navy-900/10 text-sm"
                        />
                      ) : (
                        <CellValue col={col} value={mat.values[key]} />
                      )}
                    </td>
                  );
                })}
                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                  {editingId === mat.id ? (
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={onSave} className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg hover:bg-emerald-600 transition-colors">
                        <Check size={14} /> Save
                      </button>
                      <button onClick={onCancel} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-200 transition-colors">
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button onClick={() => onStartEdit(mat)} title="Edit" className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => typeof onViewLogs === "function" && onViewLogs(mat.id)} title="History" className="p-2 text-slate-400 hover:text-navy-700 hover:bg-slate-100 rounded-lg transition-colors">
                        <History size={15} />
                      </button>
                      <button onClick={() => onDelete(mat.id)} title="Delete" className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="block md:hidden divide-y divide-slate-100">
        {materials.map((mat) => (
          <div key={mat.id} className="p-4">
            {editingId === mat.id ? (
              <div className="space-y-3">
                {columns.map((col) => {
                  const key = normalizeKey(col);
                  return (
                    <div key={col}>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{col}</label>
                      <input
                        name={key}
                        value={editedRow[key] || ""}
                        onChange={(e) => onChange(e, onEdit)}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-navy-900/10 text-sm"
                      />
                    </div>
                  );
                })}
                <div className="flex gap-2 pt-2">
                  <button onClick={onSave} className="flex-1 py-2 bg-emerald-500 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-1">
                    <Check size={15} /> Save
                  </button>
                  <button onClick={onCancel} className="flex-1 py-2 bg-slate-100 text-slate-600 font-bold rounded-xl text-sm flex items-center justify-center gap-1">
                    <X size={15} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded-md text-slate-600 font-bold inline-block mb-1">
                      {mat.values[normalizeKey(columns[0])] || "—"}
                    </p>
                    <p className="font-bold text-navy-900">{mat.values[normalizeKey(columns[1])] || "—"}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => typeof onViewLogs === "function" && onViewLogs(mat.id)} className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-lg transition-colors">
                      <History size={15} />
                    </button>
                    <button onClick={() => onStartEdit(mat)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <Edit2 size={15} />
                    </button>
                    <button onClick={() => onDelete(mat.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {columns.slice(2).map((col) => {
                    const key = normalizeKey(col);
                    return (
                      <div key={col}>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-0.5">{col}</span>
                        <CellValue col={col} value={mat.values[key]} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
